import {
  deleteModularBenefit,
  getModularBenefitById,
  saveModularBenefit,
} from '@ben/columbia/api';
import BenefitConfig from '@ben/columbia/components/benefit-config';
import AddNewVersionModal from '@ben/columbia/components/benefit-config/add-new-version';
import BenefitVersionBanner from '@ben/columbia/components/benefit-config/benefit-version-banner';
import PageHeader from '@ben/columbia/components/benefit-config/page-header';
import { useColumbiaErrors } from '@ben/columbia/contexts';
import { useInvalidateScheduledVersion } from '@ben/columbia/hooks/use-query-scheduled-version';
import { toDisplayString } from '@ben/common/string';
import RiErrorWarningFill from '@ben/icons/error-warning-fill';
import { ButtonVariant } from '@ben/orion/deprecated-button';
import Loader from '@ben/orion/loader';
import { Modal } from '@ben/orion/modals/modal';
import { PageHeaderBanner } from '@ben/orion/page-header';
import { type IModularBenefitDetail } from '@ben/shared/types/columbia';
import { WindowSubTypeEnum } from '@ben/shared/types/modular-benefit';
import { useDisclosure } from '@mantine/hooks';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { get, isEmpty } from 'lodash';
import { useCallback, useState } from 'react';
import type { Location } from 'react-router-dom';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import styled, { css } from 'styled-components';

function BenefitDetailScreen() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const location: Location<{ initialBenefit?: IModularBenefitDetail }> =
    useLocation();
  const { benefitId, versionId } = useParams<{
    benefitId: string;
    versionId: string;
  }>();
  const isScheduledVersion = !!versionId;
  const { setErrors } = useColumbiaErrors();

  const isCloneBenefit = location.pathname.includes('/benefits/clone/');
  const { data } = useQuery(
    ['columbiaBenefitDetail', benefitId, versionId],
    () => getModularBenefitById(benefitId, versionId),
    { enabled: !location.state?.initialBenefit }
  );

  const benefit = location.state?.initialBenefit || data;
  const isCreateBenefit = benefitId === 'create';
  const isSharedBenefit = !!benefit?.sharedBenefit;
  const queryParameters = new URLSearchParams(location.search);
  const searchQueryParameter = queryParameters.get('search');

  const [
    isAddVersionModalOpen,
    { open: openAddVersionModal, close: closeAddVersionModal },
  ] = useDisclosure();
  const [isConfirmDeleteOpen, setIsConfirmDeleteOpen] = useState(false);

  const saveMutation = useMutation({
    mutationFn: ({
      updatedBenefit,
      id,
    }: {
      updatedBenefit: IModularBenefitDetail;
      id?: string;
      isScheduledVersion?: boolean;
    }) => saveModularBenefit(updatedBenefit, id, versionId),
    onSuccess: ({ id: savedBenefitId, warnings }) => {
      void queryClient.invalidateQueries(['columbiaBenefitList']);
      void queryClient.invalidateQueries([
        'columbiaBenefitDetail',
        benefitId,
        versionId,
      ]);
      toast.success('Benefit saved successfully');
      for (const warning of warnings) {
        toast.warning(warning);
      }
      if (isCreateBenefit || isCloneBenefit) {
        navigate({
          pathname: `/benefits/${savedBenefitId}`,
          search: searchQueryParameter
            ? `search=${searchQueryParameter}`
            : undefined,
        });
      }
      setErrors(undefined);
    },
    onError: (error: unknown) => {
      if (isAxiosError(error)) {
        setErrors(error.response?.data as Record<string, unknown> | undefined);
        const errorData = error.response?.data;
        if (errorData && typeof errorData === 'object') {
          displayCreateOrUpdateBenefitError(
            errorData as Record<string, unknown[]>
          );
          return;
        }
      }

      toast.error('Error saving benefit, please try again.');
    },
  });

  const onSave = async (incomingBenefit: IModularBenefitDetail) => {
    const id = benefitId === 'create' || isCloneBenefit ? undefined : benefitId;

    const updatedBenefit = {
      ...incomingBenefit,
      id: isCloneBenefit ? undefined : incomingBenefit.id,
      recalculationConfigs: incomingBenefit.recalculationConfigs?.map(
        (config) => ({
          ...config,
          id: isCloneBenefit ? undefined : config.id,
        })
      ),
      benefitImage:
        isCloneBenefit && incomingBenefit.benefitImage
          ? {
              ...incomingBenefit.benefitImage,
              benefit: undefined,
            }
          : incomingBenefit.benefitImage,
      benefitHeroImage:
        isCloneBenefit && incomingBenefit.benefitHeroImage
          ? {
              ...incomingBenefit.benefitHeroImage,
              benefit: undefined,
            }
          : incomingBenefit.benefitHeroImage,
      additionalConfig: {
        ...incomingBenefit.additionalConfig,
        id: isCloneBenefit ? undefined : incomingBenefit.additionalConfig?.id,
        steps: incomingBenefit.additionalConfig?.steps?.map((step) => {
          const stepId = isCloneBenefit ? undefined : step.id;
          return {
            ...step,
            id: stepId,
            components: step.components?.map((component) => ({
              ...component,
              id: isCloneBenefit ? undefined : component.id,
              parentId: isCloneBenefit ? undefined : component.parentId,
              parentType: isCloneBenefit ? undefined : component.parentType,
              options: component.options?.map((option) => ({
                ...option,
                id: isCloneBenefit ? undefined : option.id,
              })),
            })),
          };
        }),
        subPrices: incomingBenefit.additionalConfig?.subPrices?.map(
          (subPrice) => ({
            ...subPrice,
            id: isCloneBenefit ? undefined : subPrice.id,
          })
        ),
      },
      benefitType: isCloneBenefit ? undefined : incomingBenefit.benefitType,
      benefitWindows: incomingBenefit?.benefitWindows?.map((benefitWindow) => ({
        ...benefitWindow,
        id: isCloneBenefit ? undefined : benefitWindow.id,
        windowSubType:
          benefitWindow.windowSubType === WindowSubTypeEnum.NONE
            ? undefined
            : benefitWindow.windowSubType,
      })),
    };

    await saveMutation.mutateAsync({ updatedBenefit, id });
  };

  const invalidateScheduledVersion = useInvalidateScheduledVersion();

  const onClickDelete = useCallback(() => setIsConfirmDeleteOpen(true), []);

  const onConfirmDelete = () => {
    if (benefitId && benefitId !== 'create') {
      setIsConfirmDeleteOpen(false);
      deleteModularBenefit(benefitId, versionId)
        .then(() => {
          const search = searchQueryParameter
            ? `search=${searchQueryParameter}`
            : undefined;
          if (versionId) {
            void invalidateScheduledVersion(benefitId);
            navigate({
              pathname: `/benefits/${benefitId}/`,
              search,
            });
            window.scrollTo(0, 0);
            toast.success('Scheduled version deleted');
          } else {
            void queryClient.invalidateQueries(['columbiaBenefitList']);
            navigate({
              pathname: '/benefits/',
              search,
            });
            toast.success('Benefit deleted successfully');
          }
        })
        .catch((error: unknown) => {
          const errorMessage = get(error, 'response.data.detail');
          toast.error(
            errorMessage ?? 'There was a problem deleting the benefit'
          );
          console.error('Deletion error', error);
        });
    }
  };

  const onClone = () => {
    navigate(`/benefits/clone/${benefitId}`);
  };

  return (
    <>
      {isSharedBenefit && (
        <PageHeaderBanner
          bannerIcon={RiErrorWarningFill}
          bannerText={
            <BannerText>
              <strong>Restricted benefit.</strong> This benefit is managed by{' '}
              <strong>{benefit.sharedBenefit?.managingEntity.name}.</strong> To
              make changes, please visit their Service Management Portal.
            </BannerText>
          }
        />
      )}
      <Wrapper>
        <PageHeader
          isSharedBenefit={isSharedBenefit}
          isCreateBenefit={isCreateBenefit}
          isCloneBenefit={isCloneBenefit}
          isScheduledVersion={isScheduledVersion}
          requiresApproval={
            benefit?.requiresActivationApproval ||
            benefit?.requiresDeactivationApproval ||
            !isEmpty(benefit?.conditionalApprovalConfigs)
          }
          benefitId={benefitId}
          benefitName={benefit?.nameEn}
          onClickClone={onClone}
          onClickAddNewVersion={() => openAddVersionModal()}
        />
        {!isCreateBenefit && !isCloneBenefit && (
          <BenefitVersionBanner
            benefit={benefit}
            benefitId={benefitId}
            versionId={versionId}
            isScheduledVersion={isScheduledVersion}
            onClickDelete={onClickDelete}
          />
        )}
        {isEmpty(benefit) ? (
          <Loader variant="full-height" />
        ) : (
          <BenefitConfig
            benefit={benefit}
            onSave={onSave}
            onDelete={onClickDelete}
            isCloneBenefit={isCloneBenefit}
            isScheduledVersion={isScheduledVersion}
          />
        )}
        <AddNewVersionModal
          opened={isAddVersionModalOpen}
          onClose={closeAddVersionModal}
          benefitId={benefitId}
        />
        <Modal
          isOpen={isConfirmDeleteOpen}
          onClose={() => setIsConfirmDeleteOpen(false)}
          title={
            isScheduledVersion
              ? 'Delete benefit version'
              : `Delete ${benefit?.nameEn}`
          }
          description={
            isScheduledVersion
              ? 'Are you sure you want to delete this renewal version? This action cannot be undone.'
              : 'This action cannot be undone and can impact the associated employers, employees, and payrolls.\n\nPlease confirm the following to proceed.'
          }
          primaryText={
            isScheduledVersion ? 'Delete renewal version' : 'Delete benefit'
          }
          primaryVariant={ButtonVariant.error}
          secondaryText="Cancel"
          agreements={
            isScheduledVersion
              ? undefined
              : [
                  'I understand that deleting a published benefit it will remove the benefit from the assigned employers.',
                  'I understand that deleting a published benefit it will remove any associated enrollments and may affect payroll.',
                ]
          }
          onConfirm={onConfirmDelete}
          width="520px"
        />
      </Wrapper>
    </>
  );
}

export default BenefitDetailScreen;

const Wrapper = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(4)};
    max-width: 886px;
    margin: 0 auto;
  `}
`;

const BannerText = styled.div`
  ${({ theme }) => css`
    strong {
      font-weight: 600;
    }
    color: ${theme.palette.neutral['00']};
  `}
`;

const displayCreateOrUpdateBenefitError = (
  errorMessage: Record<string, unknown[]>
) => {
  for (const key in errorMessage) {
    const outerValue = errorMessage[key];

    if (typeof outerValue === 'object' && outerValue !== null) {
      for (const innerKey in outerValue) {
        const innerValue = outerValue[innerKey];

        if (Array.isArray(innerValue)) {
          for (const value of innerValue)
            if (typeof value === 'object') {
              toast.error(`Error saving benefit, please check the form`);
            } else {
              const stringValue = String(value);
              const newValue = stringValue.includes(
                'ExpressionCannotBeEvaluatedError'
              )
                ? stringValue.split('ExpressionCannotBeEvaluatedError')[1]
                : stringValue;

              // the below formatting is an odd case where the error message is a stringified tuple
              // and needs to be formatted to be readable
              toast.error(
                `Error saving benefit: ${newValue
                  .replaceAll('\\', '')
                  .replaceAll('"', '')
                  .replace("(''", "'")
                  .replace("')", '')}
                `
              );
            }
        } else {
          toast.error(`Error saving benefit: ${toDisplayString(innerValue)}`);
        }
      }
    } else {
      toast.error(`Error saving benefit: ${String(outerValue)}`);
    }
  }
};
